/* eslint-disable complexity */
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import loadable from "@loadable/component";
import styles from "./styles.css";
import { getScreenContent } from "./selector";
import { PAI_STATES } from "../post-inspection-diy/constant";
import { APPOINTMENT_STATE, PRICE_DISCOVERY, PURCHASE_REQUEST_STATUS } from "../../../constants/seller-diy-constants";
import { sendCustomGaEvents } from "../../../tracking";
import { noPriceViewScreen } from "./tracking";
import { Label } from "@cars24/turbo-web/lib/label";
import { NUMBER } from "../../../constants/app-constants";
import { LinkButton } from "@cars24/turbo-web/lib/link-button";
import PostInspectionDiyModal from "../post-inspection-diy-modal";
import useToggle from "../../../hooks/use-toggle";
import { Heading } from "@cars24/turbo-web/lib/heading";
import Reject from "./images/reject.svg";
import { ButtonStack } from "@cars24/turbo-web/lib/button-stack";
import { Button } from "@cars24/turbo-web/lib/button";
import { useTokens } from "@cars24/turbo-web/lib/theme-provider";

const PostInspectionDiyPaiOcbPrice = loadable(() => import("../post-inspection-diy-pai-ocb-price"));
const PostInspectionDiyNoPriceOffered = loadable(() => import("../post-inspection-diy-no-price-offered"));
const PostInspectionDiyOfferDeductions = loadable(() => import("../post-inspection-diy-offer-deductions"));

// eslint-disable-next-line max-statements
const PostInspectionDiyAuctionOver = ({
    isNoBid,
    inspectionResponse,
    auctionResponse,
    handleStartDocumentation,
    savePriceViewConnect,
    rejectDnpOfferConnect = () => {}
}) => {
    const {
        auction = {},
        paiState,
        token,
        isPriceViewed = false,
        priceDiscovery,
        purchaseRequestStatus = "",
        appointmentState,
        appointmentId,
        isDealerBackOut,
        isGsAssuredPrice,
        featureData = {}
    } = inspectionResponse;
    const { dnpPriceConfirmationEnabled = false } = featureData || {};
    const { previousOfferedPrice, isExpired } = auctionResponse || {};
    const { isReAuction = false } = auction || {};
    const isPostLeadLost = appointmentState === APPOINTMENT_STATE.DEAL_LOST;
    const [rejectDnpConfirmation, setRejectDnpConfirmation] = useToggle(false);
    const { tokens } = useTokens();
    const content = getScreenContent({isExpired, isNoBid, isPostLeadLost, paiState, isReAuction, isGsAssuredPrice, isDealerBackOut});
    const {
        title
    } = content;
    const isPaiOcbScreenActive = priceDiscovery === PRICE_DISCOVERY.NEGOTIATE_PRICE_REQUESTED;

    useEffect(() => {
        if (!isNoBid && !isExpired && !isPriceViewed) {
            savePriceViewConnect(token);
        }
        if (isNoBid) {
            sendCustomGaEvents({
                ...noPriceViewScreen,
                eventLabel: appointmentId
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNoBid, isExpired, isPriceViewed]);

    const renderCta = () => {
        if (priceDiscovery === PRICE_DISCOVERY.ACCEPTED && purchaseRequestStatus === PURCHASE_REQUEST_STATUS.CREATED) {
            return (
                <div styleName={"styles.documentationCta"}>
                    <Button size="large" label={"Complete documentation >>>"} onClick={handleStartDocumentation}/>
                </div>
            );
        }
        return null;
    };

    const handleRejectDnp = () => {
        setRejectDnpConfirmation();
    };

    // eslint-disable-next-line complexity
    const renderLayout = () => {

        if (isPaiOcbScreenActive) {
            return (
                <PostInspectionDiyPaiOcbPrice
                    previousOfferedPrice={previousOfferedPrice}
                />
            );
        }

        if (isNoBid) {
            return (
                <PostInspectionDiyNoPriceOffered
                    showNoPriceScreen={isReAuction || paiState === PAI_STATES.PAI_PRICE_NOT_AVAILABLE}
                    previousOfferedPrice={previousOfferedPrice}
                    isDealerBackOut={isDealerBackOut}
                />
            );
        }

        return (
            <React.Fragment>
                <PostInspectionDiyOfferDeductions isGsAssuredPrice={isGsAssuredPrice} />
                {
                    dnpPriceConfirmationEnabled && priceDiscovery !== PRICE_DISCOVERY.ACCEPTED  && <div styleName="styles.dnpReject">
                        <Label fontWeight="regular" level={NUMBER.FOUR} >Not satisfied with the offer? Reject the offer and try running the auction again</Label>
                        <LinkButton
                            label="Reject offer and start new auction"
                            onClick={() => handleRejectDnp()}
                            variant="brand"
                            underLine={true}
                            href="#"
                            preventDefaultBehaviour={true}
                        />
                    </div>
                }
                {renderCta()}
            </React.Fragment>
        );
    };

    return (
        <div styleName={"styles.auctionText"}>

            {
                !isPaiOcbScreenActive &&
                <React.Fragment>
                    { title && <h4 styleName={"styles.auctionHeader"}>{title}</h4> }
                </React.Fragment>
            }
            {renderLayout()}
            {rejectDnpConfirmation &&
            <PostInspectionDiyModal
                height="auto"
                isOpen={true}
                width="520px"
                showCloseButton={true}
                onClose={() => setRejectDnpConfirmation()}
            >
                <div styleName="styles.dnpRejectModal">
                    <Heading level={NUMBER.FOUR}>You are about to lose this offer!</Heading>
                    <Label level={NUMBER.THREE} color={tokens.text_default_text_secondary} >To start a new auction, you have to reject this offer and set a new auction price.</Label>
                    <img src={Reject} alt="reject" />
                    <ButtonStack layout="vertical">
                        <Button size="large" label="Reject and re-auction" onClick={() => rejectDnpOfferConnect(false)} />
                        <Button size="large" label="Cancel" variant="secondary" onClick={() => setRejectDnpConfirmation()} />
                    </ButtonStack>
                </div>
            </PostInspectionDiyModal>
            }
        </div>
    );
};

PostInspectionDiyAuctionOver.propTypes = {
    isNoBid: PropTypes.bool,
    priceBreakup: PropTypes.object,
    inspectionResponse: PropTypes.object,
    auctionResponse: PropTypes.object,
    handleStartDocumentation: PropTypes.func,
    savePriceViewConnect: PropTypes.func,
    rejectDnpOfferConnect: PropTypes.func,
    secureToken: PropTypes.string,
    mobile: PropTypes.string,
    createOrderConenect: PropTypes.func
};

export default PostInspectionDiyAuctionOver;

